import { createTheme } from '@mui/material/styles';
import { appThemeTokens } from './theme-tokens.mjs';

const themePalette = appThemeTokens.palette;
const themeTypography = appThemeTokens.typography;
const themeShape = appThemeTokens.shape;
const themeShadows = appThemeTokens.shadows;
const appBorder = '#D1D5DB';
const appBorderLight = '#E8E8E8';
const appSurface = '#FFFFFF';
const appSurfaceMuted = '#F3F4F6';
const appText = '#1A1A1A';
const appTextMuted = '#717171';
const appShadowSm = '0 1px 3px rgba(0,0,0,0.04)';
const appShadowMd = '0 4px 12px rgba(0,0,0,0.08)';

// Jobs Jobs Jobs MUI Theme - matching existing design system
const theme = createTheme({
  palette: themePalette,
  typography: themeTypography,
  spacing: appThemeTokens.spacing,
  shape: themeShape,
  shadows: themeShadows,
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '6px 16px',
          fontWeight: 600,
          boxShadow: 'none',
          textTransform: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
        },
        sizeLarge: {
          padding: '10px 24px',
          fontSize: '0.9375rem',
        },
        sizeSmall: {
          padding: '4px 12px',
          fontSize: '0.8125rem',
        },
        sizeMedium: {
          padding: '6px 16px',
          fontSize: '0.875rem',
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
        },
        outlined: {
          backgroundColor: appSurface,
          borderColor: appBorder,
          color: appText,
          '&:hover': {
            borderColor: appText,
            backgroundColor: appSurfaceMuted,
          },
        },
        text: {
          color: appText,
          '&:hover': {
            backgroundColor: appSurfaceMuted,
          },
        },
      },
    },
    MuiCard: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          border: `1px solid ${appBorder}`,
          borderRadius: 16,
          boxShadow: appShadowSm,
          backgroundColor: appSurface,
          transition: 'all 0.2s ease',
        },
      },
      variants: [
        {
          props: { variant: 'outlined' },
          style: {
            borderColor: appBorder,
            boxShadow: 'none',
          },
        },
        {
          props: { variant: 'interactive' },
          style: {
            cursor: 'pointer',
            '&:hover': {
              borderColor: appText,
              boxShadow: appShadowMd,
              transform: 'translateY(-2px)',
            },
          },
        },
        {
          props: { variant: 'light' },
          style: {
            border: `1px solid ${appBorder}`,
            boxShadow: appShadowSm,
            backgroundColor: appSurface,
          },
        },
      ],
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          '&:last-child': {
            paddingBottom: 16,
          },
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
      },
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 10,
            backgroundColor: appSurface,
            '& fieldset': {
              borderColor: appBorder,
            },
            '&:hover fieldset': {
              borderColor: appText,
            },
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          fontWeight: 500,
        },
        outlined: {
          borderColor: appBorder,
          color: appTextMuted,
          backgroundColor: appSurface,
          fontSize: '0.75rem',
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        h1: { fontWeight: 600, color: appText },
        h2: { fontWeight: 600, color: appText },
        h3: { fontWeight: 600, color: appText },
        h4: { fontWeight: 600, color: appText },
        h5: { fontWeight: 600, color: appText },
        h6: { fontWeight: 600, color: appText },
        subtitle1: { fontWeight: 600, color: appText },
        subtitle2: { fontWeight: 600, color: appText },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: appBorder,
        },
      },
    },
    MuiAppBar: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          boxShadow: 'none',
          borderBottom: `1px solid ${appBorderLight}`,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 14,
        },
        elevation1: {
          boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)',
        },
        elevation2: {
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.06)',
        },
        elevation3: {
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08)',
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 10,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 16,
          border: `1px solid ${appBorderLight}`,
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          border: `1px solid ${appBorderLight}`,
          boxShadow: appShadowMd,
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          '&.Mui-selected': {
            backgroundColor: appText,
            color: appSurface,
            '&:hover': {
              backgroundColor: '#000000',
            },
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: appText,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          color: appTextMuted,
          '&.Mui-selected': {
            color: appText,
          },
        },
      },
    },
    MuiContainer: {
      defaultProps: {
        maxWidth: 'lg',
      },
    },
  },
});

// Common layout patterns - use these for consistent spacing
export const layoutPatterns = {
  // Page layouts
  pageContainer: {
    py: 3,
  },
  pageHeader: {
    mb: 4,
  },
  
  // Flex patterns
  flexRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 2,
  },
  flexRowSpaceBetween: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  flexRowEnd: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: 1,
  },
  flexColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  flexWrap: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 1,
  },
  
  // Section spacing
  section: {
    mb: 3,
  },
  subsection: {
    mb: 2,
  },
  
  // Card spacing
  cardSpacing: {
    mb: 3,
  },
  cardContent: {
    p: 2,
  },
  
  // Center content
  centerContent: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '60vh',
  },
  
  // Full width
  fullWidth: {
    width: '100%',
  },
};

// Clean Design System - Reusable style objects
export const designSystem = {
  // Standard light card
  card: {
    light: {
      border: '1px solid',
      borderColor: 'grey.200',
      borderRadius: 2,
      boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
      bgcolor: 'white',
    },
    interactive: {
      border: '1px solid',
      borderColor: 'grey.200',
      borderRadius: 2,
      boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
      bgcolor: 'white',
      transition: 'all 0.2s ease',
      cursor: 'pointer',
      '&:hover': {
        borderColor: 'primary.main',
        boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
        transform: 'translateY(-2px)',
      },
    },
  },
  
  // Button styles
  button: {
    contained: {
      boxShadow: 'none',
      '&:hover': { boxShadow: 'none' },
    },
    outlined: {
      bgcolor: 'white',
      borderColor: 'grey.300',
      '&:hover': { 
        borderColor: 'primary.main', 
        bgcolor: 'primary.50',
      },
    },
  },
  
  // Typography
  heading: {
    fontWeight: 600,
    color: 'text.primary',
  },
  
  // Chips
  chip: {
    outlined: {
      borderColor: 'grey.300',
      color: 'text.secondary',
      bgcolor: 'white',
      fontSize: '0.75rem',
    },
  },
  
  // Content boxes
  infoBox: {
    light: {
      p: 2.5,
      bgcolor: 'white',
      borderRadius: 2,
      border: '1px solid',
      borderColor: 'grey.200',
      boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
    },
    accent: {
      p: 2.5,
      bgcolor: 'white',
      borderRadius: 2,
      border: '1px solid',
      borderColor: 'grey.200',
      borderLeft: '4px solid',
      borderLeftColor: 'primary.main',
      boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
    },
  },
  
  // Dividers
  divider: {
    borderColor: 'grey.200',
  },
};

export default theme;
